import { createAsyncThunk } from '@reduxjs/toolkit';
import { type RootState, type AppDispatch } from '../../../stores/redux/setup';
import { sendMessageMany } from '../../realtime/realtime.slice';
import { getMemberIds } from '../../realtime/realtime.selectors';
import {
  RealtimeMessageType,
  type ExtensionSideloadSyncPayload,
} from '../../realtime/realtime.types';
import { bridge } from '../instances';
import { getSideloadBaseUrl } from './sideloadBaseUrlRegistry';
import { isProduction } from '../../../shared/isProduction';

/**
 * Sends the current sideloaded extension set to all joined realtime members.
 * Used after sideload and when membership changes so late joiners receive sync.
 */
export const syncSideloadedExtensionsToMembers = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  'extensions/syncSideloadedExtensionsToMembers',
  async (_, { dispatch, getState }) => {
    if (isProduction()) {
      return;
    }

    const sideloads: ExtensionSideloadSyncPayload['sideloads'] = [];
    for (const id of bridge.getInstanceIds()) {
      const instance = bridge.getInstance(id);
      if (instance.source !== 'sideload') {
        continue;
      }
      const baseUrl = getSideloadBaseUrl(instance.manifest.name);
      if (baseUrl === undefined) {
        continue;
      }
      sideloads.push({
        baseUrl,
        manifest: instance.manifest,
      });
    }

    const memberIds = getMemberIds(getState());
    if (sideloads.length === 0 || memberIds.length === 0) {
      return;
    }

    await dispatch(
      sendMessageMany({
        ids: memberIds,
        message: {
          messageType: RealtimeMessageType.ExtensionSideloadSync,
          payload: { sideloads },
        },
      }),
    );
  },
);
